/* istanbul ignore file */

import loadable from "@loadable/component";
import { graphql } from "gatsby";
import React from "react";

import type { SectionsLayoutDataFragment } from "../../../graphql-types";
import CheetahEmbed from "../CheetahEmbed";
import CampaignWizard, { CampaignWizardHead } from "./CampaignWizard";

const BenefitsList = loadable(() => import("./BenefitsList"));
const CtaButton = loadable(() => import("./CtaButton"));
const DisclaimerMessage = loadable(() => import("./DisclaimerMessage"));
const Heading = loadable(() => import("./Heading"));
const HowToInstructions = loadable(() => import("./HowToInstructions"));
const Image = loadable(() => import("./Image"));
const Paragraph = loadable(() => import("./Paragraph"));
const ProductsGrid = loadable(() => import("./ProductsGrid"));
const TipsList = loadable(() => import("./TipsList"));
const VideoPlayerSection = loadable(() => import("./VideoPlayerSection"));
const ImageLinkGrid = loadable(() => import("./ImageLinkGrid"));
const VideoCarouselSection = loadable(() => import("./VideoCarouselSection"));
type Props = {
  sections: SectionsLayoutDataFragment[];
};

export default function SectionsLayout({ sections }: Props) {
  return (
    <div className="flex flex-col gap-12 mb-20 text-gray-500">
      {sections.map((section) => {
        // see https://www.gatsbyjs.com/blog/flexible-fine-grained-code-splitting-with-gatsby-loadable-components/
        switch (section.__typename) {
          case "BenefitsList":
            return <BenefitsList {...section} />;
          case "DisclaimerMessage":
            return <DisclaimerMessage {...section} />;
          case "Heading":
            return <Heading {...section} />;
          case "HowToInstructions":
            return <HowToInstructions {...section} />;
          case "Image":
            return <Image {...section} />;
          case "ProductsGrid":
            return <ProductsGrid {...section} />;
          case "CtaButton":
            return <CtaButton {...section} />;
          case "Video":
            return <VideoPlayerSection {...section} />;
          case "Paragraph":
            return <Paragraph {...section} />;
          case "CampaignWizard":
            return <CampaignWizard {...section} />;
          case "TipsList":
            return <TipsList {...section} />;
          case "ImageLinkGrid":
            return <ImageLinkGrid {...section} />;
          case "CheetahEmbed":
            return <CheetahEmbed {...section} />;
          case "VideoCarousel":
            return <VideoCarouselSection {...section} />;
          default:
            throw new Error(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              `Unhandled component type: ${(section as any).__typename}`
            );
        }
      })}
    </div>
  );
}

export const fragment = graphql`
  fragment SectionsLayoutData on Section {
    __typename
    ... on Node {
      key: id
    }
    ... on Heading {
      ...HeadingData
    }
    ... on Image {
      ...ImageData
    }
    ... on BenefitsList {
      ...BenefitsListData
    }
    ... on HowToInstructions {
      ...HowToInstructionsData
    }
    ... on DisclaimerMessage {
      ...DisclaimerMessageData
    }
    ... on ProductsGrid {
      ...ProductsGridData
    }
    ... on CtaButton {
      ...CtaButtonData
    }
    ... on Video {
      autoPlay
      video {
        description
        cdnVideo {
          path
          posterPath
        }
      }
    }
    ... on Paragraph {
      ...ParagraphData
    }
    ... on CampaignWizard {
      ...CampaignWizardData
    }
    ... on TipsList {
      ...TipsListData
    }
    ... on ImageLinkGrid {
      ...ImageLinkGridData
    }
    ... on CheetahEmbed {
      ...CheetahEmbedData
    }
    ... on VideoCarousel {
      ...VideoCarouselData
    }
  }
`;

export function SectionsLayoutHead({ sections }: Props) {
  return (
    <>
      {sections.map((section) => {
        // see https://www.gatsbyjs.com/blog/flexible-fine-grained-code-splitting-with-gatsby-loadable-components/
        switch (section.__typename) {
          case "CampaignWizard":
            return <CampaignWizardHead />;
        }
      })}
    </>
  );
}
